/* ============================================================
   Best Sellers Flash Sale — landing page behaviour
   - Countdown timer (fixed end date OR evergreen daily reset)
   - AJAX add-to-cart for single-variant products
   - Sticky-bar body offset
   Vanilla JS, no globals except one namespaced init guard.
   Re-initialises safely on Shopify Theme Editor section reloads.
   ============================================================ */
(function () {
  'use strict';

  /* The script tag is included by more than one section; run once. */
  if (window.__wsFlashSaleInit) return;
  window.__wsFlashSaleInit = true;

  var timerHandle = null;

  /* ---------- Countdown ---------- */

  function getConfig() {
    /* Master config is emitted by the Flash Sale Hero section. */
    var el = document.querySelector('[data-ws-countdown-config]');
    if (!el) return null;
    var cfg = {
      mode: el.getAttribute('data-mode') || 'daily',
      end: null,
      anchor: null,
      expired: el.getAttribute('data-expired-text') || ''
    };
    if (cfg.mode === 'fixed') {
      var raw = el.getAttribute('data-end') || '';
      var parsed = new Date(raw);
      if (raw && !isNaN(parsed.getTime())) {
        cfg.end = parsed;
      } else {
        cfg.mode = 'daily'; /* unparseable date — fall back to evergreen */
      }
    } else if (cfg.mode === 'weekly') {
      /* Shared UTC anchor: every visitor computes the same cycle from
         the same fixed timestamp — refreshes can never reset it. */
      var rawAnchor = el.getAttribute('data-anchor') || '';
      var parsedAnchor = new Date(rawAnchor);
      cfg.anchor = rawAnchor && !isNaN(parsedAnchor.getTime())
        ? parsedAnchor.getTime()
        : Date.UTC(2026, 7, 10); /* fallback anchor: 2026-08-10T00:00:00Z */
    }
    return cfg;
  }

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function tick() {
    var cfg = getConfig();
    var clocks = document.querySelectorAll('[data-ws-clock]');
    var chips = document.querySelectorAll('[data-ws-clock-chip]');

    if (!cfg) {
      /* No hero countdown on the page — hide every dependent clock chip. */
      chips.forEach(function (c) { c.hidden = true; });
      return;
    }

    var now = Date.now();
    var end;
    if (cfg.mode === 'fixed') {
      end = cfg.end.getTime();
    } else if (cfg.mode === 'weekly') {
      /* Pure timestamp maths: the current cycle's end is derived from
         the anchor on every tick, so the countdown stays accurate in
         inactive tabs and rolls into the next 7-day cycle by itself —
         even if the page was closed when the previous cycle expired. */
      var CYCLE = 604800000; /* 7 days in ms */
      var intoCycle = (((now - cfg.anchor) % CYCLE) + CYCLE) % CYCLE;
      end = now - intoCycle + CYCLE;
    } else {
      /* Evergreen daily: counts down to the visitor's local midnight. */
      var midnight = new Date();
      midnight.setHours(23, 59, 59, 999);
      end = midnight.getTime();
    }

    var diff = Math.max(0, end - now);
    var dEl = document.querySelector('[data-ws-cd-d]');
    var d = Math.floor(diff / 86400000);
    var hTotal = Math.floor(diff / 3600000);
    var hInDay = Math.floor((diff % 86400000) / 3600000);
    var h = dEl ? hInDay : hTotal; /* no Days box → hours carry the days */
    var m = Math.floor((diff % 3600000) / 60000);
    var s = Math.floor((diff % 60000) / 1000);

    var text;
    if (d > 0) {
      text = d + 'd ' + pad(hInDay) + ':' + pad(m) + ':' + pad(s);
    } else {
      text = (hTotal > 99 ? hTotal : pad(hTotal)) + ':' + pad(m) + ':' + pad(s);
    }
    clocks.forEach(function (el) { el.textContent = text; });

    var hEl = document.querySelector('[data-ws-cd-h]');
    var mEl = document.querySelector('[data-ws-cd-m]');
    var sEl = document.querySelector('[data-ws-cd-s]');
    if (dEl) dEl.textContent = pad(d);
    if (hEl) hEl.textContent = h > 99 ? String(h) : pad(h);
    if (mEl) mEl.textContent = pad(m);
    if (sEl) sEl.textContent = pad(s);

    /* Fixed-date campaigns can genuinely expire. */
    var isExpired = cfg.mode === 'fixed' && diff <= 0;
    document.querySelectorAll('[data-ws-countdown]').forEach(function (el) {
      el.hidden = isExpired;
    });
    document.querySelectorAll('[data-ws-expired-msg]').forEach(function (el) {
      el.hidden = !isExpired;
      if (isExpired && cfg.expired) el.textContent = cfg.expired;
    });
    if (isExpired) chips.forEach(function (c) { c.hidden = true; });
  }

  function startTimer() {
    if (timerHandle) clearInterval(timerHandle);
    tick();
    timerHandle = setInterval(tick, 1000);
  }

  /* ---------- Sticky bar: keep it from covering page content ---------- */

  function padBodyForStickyBar() {
    var bar = document.querySelector('.ws-sticky-bar');
    var pad = bar && !bar.hidden ? bar.offsetHeight + 'px' : '';
    if (document.body.style.paddingBottom !== pad) {
      document.body.style.paddingBottom = pad;
    }
  }

  /* ---------- AJAX add to cart (single-variant products only) ---------- */

  function handleAtcSubmit(event) {
    var form = event.target.closest('form[data-ws-atc]');
    if (!form) return;
    event.preventDefault();

    var button = form.querySelector('[data-ws-atc-button]');
    var errorEl = form.querySelector('[data-ws-atc-error]');
    if (!button || button.disabled) return;

    var addedLabel = form.getAttribute('data-ws-added-text') || '✓ ADDED TO CART';
    var originalLabel = button.getAttribute('data-ws-original-text') || button.textContent;
    button.setAttribute('data-ws-original-text', originalLabel);
    button.disabled = true;
    button.textContent = '…';
    if (errorEl) errorEl.hidden = true;

    var addUrl = form.getAttribute('action') || '/cart/add';
    if (addUrl.indexOf('.js') === -1) addUrl += '.js';

    fetch(addUrl, {
      method: 'POST',
      body: new FormData(form),
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok) throw json;
          return json;
        });
      })
      .then(function (item) {
        button.disabled = false;
        button.classList.add('ws-added');
        button.textContent = addedLabel;
        setTimeout(function () {
          button.classList.remove('ws-added');
          button.textContent = originalLabel;
        }, 2500);

        /* Let themes with an AJAX cart react if they listen for anything
           standard; harmless no-op otherwise. */
        document.documentElement.dispatchEvent(
          new CustomEvent('ws-flash:cart-added', { bubbles: true, detail: { item: item } })
        );

        if (form.getAttribute('data-ws-after-add') === 'cart') {
          window.location.href = form.getAttribute('data-ws-cart-url') || '/cart';
        }
      })
      .catch(function (err) {
        button.disabled = false;
        button.textContent = originalLabel;
        if (errorEl) {
          errorEl.textContent =
            (err && (err.description || err.message)) || 'Could not add to cart. Please try again.';
          errorEl.hidden = false;
        }
      });
  }

  /* ---------- Real variant selector (option rows → exact variant) ----
     Liquid embeds each card's REAL variant matrix (id, price,
     compare-at, availability, option values) as JSON, and renders one
     pill row per real Shopify option. This code only resolves the
     clicked option combination against that server-rendered matrix
     and swaps between real variant values — nothing is calculated or
     fetched client-side. Shared for all cards via event delegation. */

  function setText(root, selector, value) {
    var el = root.querySelector(selector);
    if (el) el.textContent = value;
  }

  function cardVariants(card) {
    if (card.__wsVariants !== undefined) return card.__wsVariants;
    var script = card.querySelector('script[data-ws-variants]');
    card.__wsVariants = null;
    if (script) {
      try { card.__wsVariants = JSON.parse(script.textContent); } catch (e) { /* malformed JSON: selector stays inert */ }
    }
    return card.__wsVariants;
  }

  function cardRows(card) {
    return Array.prototype.slice.call(card.querySelectorAll('.ws-opt-row'));
  }

  function selectedCombo(card) {
    return cardRows(card).map(function (row) {
      var sel = row.querySelector('.ws-opt.is-selected');
      return sel ? sel.getAttribute('data-value') : null;
    });
  }

  function matchVariant(variants, combo, colour) {
    /* Resolve pill combo (pack/size) plus, when given, the selected
       colour. Prefer an available variant; colour '' means "any
       colour, prefer available" (covers products without thumbs). */
    var fallback = null;
    for (var i = 0; i < variants.length; i++) {
      var opts = variants[i].opts;
      var hit = true;
      for (var j = 0; j < combo.length; j++) {
        if (opts[j] !== combo[j]) { hit = false; break; }
      }
      if (!hit) continue;
      if (colour && variants[i].colour !== colour) continue;
      if (variants[i].avail) return variants[i];
      if (!fallback) fallback = variants[i];
    }
    return fallback;
  }

  function selectedColour(card) {
    var t = card.querySelector('[data-ws-thumb].is-selected');
    return t ? t.getAttribute('data-colour') : '';
  }

  /* ---------- Main-image gallery (real parent-product media) ---------- */

  function cardGallery(card) {
    if (card.__wsGallery !== undefined) return card.__wsGallery;
    var script = card.querySelector('script[data-ws-gallery]');
    card.__wsGallery = null;
    if (script) {
      try { card.__wsGallery = { list: JSON.parse(script.textContent), idx: 0 }; } catch (e) { /* inert */ }
    }
    return card.__wsGallery;
  }

  function setMainImage(card, url) {
    /* INSTANT swap: src changes synchronously (images are preloaded
       at init, so the browser serves them from cache) with a short
       non-blocking fade-in. No timers gate the navigation. */
    var img = card.querySelector('.ws-img img');
    if (!img || !url || img.getAttribute('src') === url) return;
    img.src = url;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.classList.remove('ws-img-fade');
    void img.offsetWidth; /* restart the fade animation */
    img.classList.add('ws-img-fade');
  }

  function preloadGalleries() {
    /* Warm the browser cache with every card's real gallery images
       once per page load — arrow clicks then never wait on the
       network. Uses the already-rendered Shopify CDN URLs; no extra
       API calls. */
    document.querySelectorAll('script[data-ws-gallery]').forEach(function (script) {
      var card = script.closest('.ws-card');
      if (!card) return;
      var g = cardGallery(card);
      if (!g || g.preloaded) return;
      g.preloaded = true;
      g.list.forEach(function (url) {
        var im = new Image();
        im.src = url;
      });
    });
  }

  function handleGalleryNav(event) {
    var target = event.target;
    var prev = target.closest('[data-ws-gprev]');
    var next = target.closest('[data-ws-gnext]');
    if (!prev && !next) return false;
    event.preventDefault();
    event.stopPropagation();
    var card = (prev || next).closest('.ws-card');
    if (!card) return true;
    var g = cardGallery(card);
    if (!g || !g.list.length) return true;
    var step = next ? 1 : -1;
    /* index updates synchronously: one click = exactly one step */
    g.idx = ((g.idx + step) % g.list.length + g.list.length) % g.list.length;
    var url = g.list[g.idx];
    setMainImage(card, url);
    /* Keep colour/variant state in sync when this image is a real
       colour variant's image (association provided by Shopify data). */
    var thumbs = card.querySelectorAll('[data-ws-thumb]');
    for (var i = 0; i < thumbs.length; i++) {
      if (thumbs[i].getAttribute('data-img') === url) {
        if (!thumbs[i].hidden && !thumbs[i].classList.contains('is-selected')) {
          thumbs[i].click(); /* full colour selection: price/variant/CTA follow */
        }
        break;
      }
    }
    return true;
  }

  function selectThumb(card, btn) {
    card.querySelectorAll('[data-ws-thumb]').forEach(function (t) {
      var sel = t === btn;
      t.classList.toggle('is-selected', sel);
      t.setAttribute('aria-pressed', sel ? 'true' : 'false');
    });
    /* Show this colour's real variant image; keep the gallery index
       in sync so the arrows continue from that image. */
    var src = btn.getAttribute('data-img');
    if (src) {
      setMainImage(card, src);
      var g = cardGallery(card);
      if (g) {
        var i = g.list.indexOf(src);
        if (i >= 0) g.idx = i;
      }
    }
  }

  function refreshThumbs(card, variants, combo) {
    /* A colour stays visible only while it has an AVAILABLE variant
       matching the currently selected pack/size combo — live Shopify
       availability, no manual list. */
    var thumbs = card.querySelectorAll('[data-ws-thumb]');
    if (!thumbs.length) return;
    var visible = 0;
    thumbs.forEach(function (t) {
      var col = t.getAttribute('data-colour');
      var ok = false;
      for (var k = 0; k < variants.length; k++) {
        var v = variants[k];
        if (!v.avail || v.colour !== col) continue;
        var fits = true;
        for (var j = 0; j < combo.length; j++) {
          if (v.opts[j] !== combo[j]) { fits = false; break; }
        }
        if (fits) { ok = true; break; }
      }
      t.hidden = !ok;
      if (ok) visible++;
    });
    var row = card.querySelector('[data-ws-thumbs]');
    if (row) row.hidden = visible === 0;
    /* Never leave a hidden colour selected */
    var sel = card.querySelector('[data-ws-thumb].is-selected');
    if (sel && sel.hidden) {
      var first = Array.prototype.filter.call(thumbs, function (t) { return !t.hidden; })[0];
      if (first) selectThumb(card, first);
    }
  }

  function paintOptionStates(card, variants, combo) {
    cardRows(card).forEach(function (row, i) {
      row.querySelectorAll('.ws-opt').forEach(function (btn) {
        var val = btn.getAttribute('data-value');
        /* Candidates: variants with this value here and the current
           selection in every OTHER row. */
        var exists = false;
        var anyAvail = false;
        for (var k = 0; k < variants.length; k++) {
          var opts = variants[k].opts;
          if (opts[i] !== val) continue;
          var fits = true;
          for (var j = 0; j < combo.length; j++) {
            if (j !== i && opts[j] !== combo[j]) { fits = false; break; }
          }
          if (!fits) continue;
          exists = true;
          if (variants[k].avail) { anyAvail = true; break; }
        }
        btn.disabled = !exists;
        btn.classList.toggle('ws-opt-off', exists && !anyAvail);
        var isSel = val === combo[i];
        btn.classList.toggle('is-selected', isSel);
        btn.setAttribute('aria-pressed', isSel ? 'true' : 'false');
      });
    });
  }

  function applyVariant(card, v) {
    /* All values are server-rendered per-piece unit prices (plain
       text, money-formatted by Liquid). Only real variant data is
       ever displayed. */
    setText(card, '[data-ws-price-amount]', v.price);
    var cmpEl = card.querySelector('[data-ws-compare]');
    if (cmpEl) {
      setText(cmpEl, '[data-ws-compare-amount]', v.compare || '');
      cmpEl.hidden = !v.compare;
    }
    var offEl = card.querySelector('[data-ws-badge-off]');
    if (offEl) {
      if (v.off > 0) {
        offEl.textContent = '-' + v.off + '%';
        offEl.hidden = false;
      } else {
        offEl.hidden = true;
      }
    }
    /* Pack line for the selected variant: "Pack of N = £T · RRP …".
       The pack half hides for single-piece variants; the whole line
       hides only when it would be empty (no pack AND no RRP). */
    var packEl = card.querySelector('[data-ws-packinfo]');
    if (packEl) {
      var packPart = packEl.querySelector('[data-ws-packpart]');
      var hasRrp = packEl.getAttribute('data-has-rrp') === '1';
      var alwaysPack = packEl.getAttribute('data-always-pack') === '1';
      if ((v.pqty > 1 || alwaysPack) && v.ptotal) {
        setText(packEl, '[data-ws-pack-qty]', String(v.pqty));
        setText(packEl, '[data-ws-pack-total]', v.ptotal);
        if (packPart) packPart.hidden = false;
        packEl.hidden = false;
      } else {
        if (packPart) packPart.hidden = true;
        packEl.hidden = !hasRrp;
      }
      if (v.rrp) setText(packEl, '[data-ws-rrp]', v.rrp);
    }
    /* Calculated margin box for the selected variant */
    var box = card.querySelector('[data-ws-profit-box][data-ws-dynamic]');
    if (box) {
      if (v.profit) {
        var qty = v.pqty > 0 ? v.pqty : 1;
        var packOne = box.getAttribute('data-ws-pack-one') === '1';
        setText(box, '[data-ws-profit-label]', (qty > 1 || packOne) ? 'Your profit per pack' : 'Your profit per piece');
        setText(box, '[data-ws-profit-amount]', v.profit);
        var e3pack = box.querySelector('[data-ws-e3-pack]');
        var e3piece = box.querySelector('[data-ws-e3-piece]');
        if (qty > 1 || packOne) {
          setText(box, '[data-ws-profit-units]', String(qty));
          if (v.rrp) setText(box, '[data-ws-e3-rrp]', v.rrp);
          if (v.rval) setText(box, '[data-ws-e3-rval]', v.rval);
          setText(box, '[data-ws-e3-profit]', v.profit);
          setText(box, '[data-ws-profit-markup]', String(v.markup));
          if (e3pack) e3pack.hidden = false;
          if (e3piece) e3piece.hidden = true;
        } else {
          if (v.rrp) setText(box, '[data-ws-e3-rrp2]', v.rrp);
          if (v.rrp) setText(box, '[data-ws-e3-rval2]', v.rrp);
          setText(box, '[data-ws-e3-profit2]', v.profit);
          setText(box, '[data-ws-profit-markup2]', String(v.markup));
          if (e3pack) e3pack.hidden = true;
          if (e3piece) e3piece.hidden = false;
        }
        box.hidden = false;
      } else {
        box.hidden = true;
      }
    }
    /* Carry the selection to the product page */
    var ctaLink = card.querySelector('[data-ws-cta-link]');
    if (ctaLink) {
      var base = ctaLink.getAttribute('data-base-url') || ctaLink.getAttribute('href').split('?')[0];
      ctaLink.setAttribute('href', base + '?variant=' + v.id);
    }
  }

  function handleVariantClick(event) {
    var btn = event.target.closest('[data-ws-optbtn]');
    if (!btn || btn.disabled) return;
    var card = btn.closest('.ws-card');
    if (!card) return;
    var variants = cardVariants(card);
    if (!variants || !variants.length) return;

    var idx = parseInt(btn.getAttribute('data-opt-index'), 10);
    var val = btn.getAttribute('data-value');
    var combo = selectedCombo(card);
    if (idx < 0 || idx >= combo.length) return;
    combo[idx] = val;

    var colour = selectedColour(card);
    var v = matchVariant(variants, combo, colour);
    if (!v || !v.avail) {
      /* Combination missing or sold out: prefer an AVAILABLE variant
         with the clicked value — same colour first, then any colour —
         adjusting the other rows to a combination that really exists. */
      var sameColour = null;
      var anyColour = null;
      var dead = null;
      for (var k = 0; k < variants.length; k++) {
        var c = variants[k];
        if (c.opts[idx] !== val) continue;
        if (c.avail) {
          if (colour && c.colour === colour && !sameColour) sameColour = c;
          if (!anyColour) anyColour = c;
        } else if (!dead) {
          dead = c;
        }
      }
      var pick = sameColour || anyColour || v || dead;
      if (pick) {
        v = pick;
        combo = pick.opts.slice();
      }
    }
    if (!v) return;

    /* The resolution may have switched colour — reflect it */
    if (v.colour) {
      var thumb = card.querySelector('[data-ws-thumb][data-colour="' + (window.CSS && CSS.escape ? CSS.escape(v.colour) : v.colour) + '"]');
      if (thumb) selectThumb(card, thumb);
    }
    paintOptionStates(card, variants, combo);
    refreshThumbs(card, variants, combo);
    applyVariant(card, v);
  }

  function handleThumbClick(event) {
    var btn = event.target.closest('[data-ws-thumb]');
    if (!btn || btn.hidden) return;
    var card = btn.closest('.ws-card');
    if (!card) return;
    var variants = cardVariants(card);
    if (!variants || !variants.length) return;

    var colour = btn.getAttribute('data-colour');
    var combo = selectedCombo(card);
    var v = matchVariant(variants, combo, colour);
    if (!v || !v.avail) {
      /* Keep the clicked colour; adjust pack/size rows to an
         available combination in that colour. */
      for (var k = 0; k < variants.length; k++) {
        if (variants[k].colour === colour && variants[k].avail) {
          v = variants[k];
          combo = v.opts.slice();
          break;
        }
      }
    }
    if (!v) return;

    selectThumb(card, btn);
    paintOptionStates(card, variants, combo);
    refreshThumbs(card, variants, combo);
    applyVariant(card, v);
  }

  function initVariantCards() {
    document.querySelectorAll('.ws-card').forEach(function (card) {
      var variants = cardVariants(card);
      if (!variants || !variants.length) return;
      var combo = selectedCombo(card);
      if (combo.indexOf(null) !== -1) return;
      paintOptionStates(card, variants, combo);
      refreshThumbs(card, variants, combo);
      var v = matchVariant(variants, combo, selectedColour(card));
      if (v) applyVariant(card, v);
    });
  }

  /* ---------- Init & Theme Editor support ---------- */

  function init() {
    startTimer();
    padBodyForStickyBar();
    initVariantCards();
    preloadGalleries();
  }

  document.addEventListener('click', function (event) {
    if (handleGalleryNav(event)) return;
    if (event.target.closest('[data-ws-thumb]')) {
      handleThumbClick(event);
    } else {
      handleVariantClick(event);
    }
  });
  document.addEventListener('submit', handleAtcSubmit);
  window.addEventListener('resize', padBodyForStickyBar);

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:section:unload', function () {
      /* Section removed in the editor — recalc offsets and clocks. */
      setTimeout(init, 50);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
